<template>
    <div class="flex items-baseline">
        <span class="setting-header">Samtykke</span>
        <span class="ml-2">{{ getUser?.email }}</span>
        <div class="grow"/>
        <new-consent-editor v-if="isOnboarded">
            <template #default="{showEditor}">
                <UButton @click="showEditor">Opprett samtykke</UButton>
            </template>
        </new-consent-editor>
        <UButton
            variant="ghost"
            size="sm"
            icon="i-ph-arrows-clockwise"
            title="Hent samtykke"
            @click="fetchConsents"
        />
    </div>
    <div class="py-2">
        <div v-if="consents && consents.length > 0">
            <table class="w-full setting-group">
                <thead>
                    <tr>
                        <th>Leverandør</th>
                        <th>Status</th>
                        <th>Bank</th>
                        <th>Gyldig til</th>
                        <th>Valg</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="consent in consents" :key="consent.uuid">
                        <tr class="cursor-pointer bank-row select-none" @click="toggleOrSetEditingConsent(consent)">
                            <td>
                                <i
                                    class="i-ph-caret-right inline-block text-xl text-primary -mb-1 transition-all"
                                    :class="isEditingConsent(consent) ? 'rotate-90': 'rotate-0'"
                                />
                                {{ consent.origin }}
                            </td>
                            <td>{{ consent.status }}</td>
                            <td>{{ banks.find(b => b.bic === consent.bic)?.name }}</td>
                            <td>{{ consent.validUntil ? formattedDate(consent.validUntil) : '' }}</td>
                            <td>
                                <div class="flex items-center gap-2">
                                    <UButton
                                        v-if="consent.status === ConsentStatus.processing && consent.consentUrl"
                                        :to="consent.consentUrl"
                                        variant="link"
                                        color="primary"
                                        size="sm"
                                        :label="t('bank-settings.finalize-consent')"
                                        icon="i-ph-arrow-square-out"
                                        @click.stop
                                    />
                                    <UButton
                                        v-if="consent.status === ConsentStatus.active"
                                        variant="ghost"
                                        size="sm"
                                        color="error"
                                        icon="i-ph-x-circle"
                                        title="Fjern samtykke"
                                        @click.stop="() => revokeConsent(consent.uuid)"
                                    />
                                </div>
                            </td>
                        </tr>
                        <tr :class="!isEditingConsent(consent) && 'border-none'">
                            <td
                                colspan="6"
                                class="transition-all"
                                :class="isEditingConsent(consent) ? 'h-96': 'h-0'"
                            >
                                <consent-editor v-if="isEditingConsent(consent)" :consent="consent"/>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <div v-else class="flex items-center setting-group py-2">
            Det er ingen samtykke enda.
        </div>
    </div>
</template>

<script setup lang="ts">

import {useConsent} from '@/bank/consent/consent.service'
import {Consent, ConsentStatus} from '@/bank/consent/consent.types'
import ConsentEditor from '@/bank/consent/ConsentEditor.vue'
import NewConsentEditor from '@/bank/consent/NewConsentEditor.vue'
import {getUser} from '@/services/infrastructure/auth.service'
import {useOnboarding} from '@/bank/onboarding/onboarding.service'
import {useBanks} from '@/bank/banks.service'
import {formattedDate} from '@/services/formatter.service'
import {onMounted, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {useRouter} from 'vue-router'

const {isOnboarded} = useOnboarding()
const {consents, checkConsentStatus, fetchConsents, revokeConsent} = useConsent()
const {t} = useI18n()
const router = useRouter()
const {banks} = useBanks()

const editingConsent = ref<Consent | undefined>(undefined)

const toggleOrSetEditingConsent = (consent: Consent) => {
    editingConsent.value = isEditingConsent(consent) ? undefined : consent
}
const isEditingConsent = (consent: Consent) => consent.uuid === editingConsent.value?.uuid

onMounted(async () => {
    const {consentUuid, ...newQuery} = router.currentRoute.value.query
    if (consentUuid) {
        await checkConsentStatus(consentUuid as string)
        await router.replace({query: newQuery})
    }
})
</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

table {
    th {
        @apply px-4;
    }

    td {
        @apply px-4;
    }

    tr {
        @apply border-b dark:border-gray-400/30 text-left;
    }
}
</style>
